import type {
  Almacenamiento, BusquedaAvanzada, ColaConversionVista, HerramientasOcr, Health, PistasDescarga,
  KnownClient, PlanManual, PreferenciasVersion, RegistroConversion, ReproduccionDescarga,
  SpeedHistory, TitleKind, TransferDetail, TransferSource, TransferStats,
} from '@film/shared';

/** Película o serie (D-27). Lo decide la categoría de la ficha en HispaShare. */
export type { TitleKind } from '@film/shared';

/** Qué hace idónea a una versión de HispaShare: lo comparte la ficha, que colorea con
 *  ello, y Ajustes, que lo edita. */
export type { Juicio, Nivel, PreferenciasVersion, Valoracion } from '@film/shared';

// Los contratos de la conversión viven en `@film/shared`: la pantalla que edita las pistas
// y el planificador que construye el ffmpeg tienen que estar de acuerdo campo a campo.
export type {
  AjustePista, AnalisisVista, AvanceOcr, BusquedaAvanzada, ColaConversionVista, FaseConversion,
  FilaPlan, ItemCola, PistaVista, PistasDescarga, PlanManual, ProgresoConversion,
} from '@film/shared';

/** La revisión de una biblioteca (D-28): el veredicto de cada película y sus motivos. */
export type {
  AnalisisRevisado, CandidataRevisada, FilaRevision, PlanRevisado, ResumenRevision, VeredictoRevision,
} from '@film/shared';
import type { FilaRevision, ResumenRevision } from '@film/shared';

export interface BibliotecaRevisable {
  id: string;
  title: string;
  serverName: string;
  localPath: string;
  /** Si cuenta para la watchlist. La revisión no lo exige. */
  countsAsOwned: boolean;
  revisadas: number;
}

export interface ResumenRevisionTarea {
  biblioteca: string;
  revisadas: number;
  saltadas: number;
  porVeredicto: Partial<ResumenRevision>;
  errores: string[];
  duracionMs: number;
}

/** El reproductor integrado (D-26): qué ficheros de una descarga se pueden poner y qué va a
 *  pasar con cada pista —directa o recodificada al vuelo— antes de pedir el primer byte. */
export type {
  AudioReproduccion, ClaseFuente, Entrega, EntregaSubtitulo, FuenteReproduccion,
  ReproduccionDescarga, SubtituloReproduccion, VideoReproduccion,
} from '@film/shared';

/** El registro de las conversiones de una descarga: qué se decidió y qué dijo ffmpeg. */
export type {
  EstadoIntento, IntentoConversion, LineaRegistro, NivelRegistro, OrigenIntento,
  RegistroConversion,
} from '@film/shared';

/** El detalle que aMule tiene de una descarga. Ya viene traducido a nuestro vocabulario
 *  por el adaptador: aquí no entra ni un `snake_case` de amuleapi. */
export type { FilePart, PartState, TransferDetail, TransferSource } from '@film/shared';

/** Lo que aMule lleva contado y su historia de velocidades. Es suyo: se le pregunta y se
 *  enseña, sin guardar nada por nuestro lado. */
export type { KnownClient, SpeedHistory, SpeedPoint, TransferStats } from '@film/shared';

/** Con quién tenemos crédito: el almacén de créditos de aMule, ya filtrado y con el
 *  modificador que se estima que cada cliente nos aplica en su cola. */
export interface ClienteConCredito extends KnownClient {
  /** De 1 a 10: por cuánto multiplica nuestro tiempo de espera en su cola. */
  credito: number;
}
export interface ResumenCreditos {
  conocidos: number;
  conCredito: number;
  conectados: number;
  clientes: ClienteConCredito[];
}

/** El espacio en disco, agrupado por sistema de ficheros y no por carpeta. */
export type { Almacenamiento, CarpetaAlmacenamiento, SistemaDeFicheros } from '@film/shared';

export interface EstadoBiblioteca {
  status: 'owned' | 'missing' | 'unknown';
  confidence: 'exact' | 'fuzzy' | null;
  resolution: string | null;
  serverName: string | null;
  libraryTitle: string | null;
  plexRatingKey: string | null;
  upgradeAvailable: boolean;
  /** Cuántos episodios de la serie hay en Plex; nulo en una película (D-27). */
  episodiosEnPlex: number | null;
}

export interface ResultadoBusqueda {
  id: string;
  kind: TitleKind;
  title: string;
  year: number | null;
  posterUrl: string | null;
  imdbRating: number | null;
  director: string | null;
  /** `Películas`, `Series`… El buscador las mezcla, y al vincular a mano distinguirlas es
   *  la mitad de elegir bien. */
  category: string | null;
  genres: string[];
  synopsisCorta: string | null;
  library: EstadoBiblioteca;
}

export interface FicheroRelease {
  id?: string | null;
  position: number;
  fileName: string;
  sizeBytes: number | null;
  kind: 'video' | 'subtitles' | 'archive' | 'other';
  season: number | null;
  episode: number | null;
  /** Hasta qué episodio llega si el fichero trae varios seguidos. */
  episodeEnd: number | null;
  /** La descarga que ya existe de este fichero, si la hay. En una serie es lo que dice qué
   *  episodios de la temporada faltan; en una película, que el botón no ofrezca lo ya pedido. */
  descarga?: { id: string; status: EstadoDescarga } | null;
  /** Ya está en Plex, entero. «Descargar temporada» lo salta. */
  enPlex?: boolean;
}

export interface ReleaseVista {
  elinkId: number;
  qualityRaw: string | null;
  resolution: string | null;
  formatRaw: string | null;
  /** `formatRaw` ya partido: `mkv (x265-EAC3)` → contenedor, códec de vídeo y de audio.
   *  Es lo que la ficha compara contra las preferencias para colorear cada columna. */
  container: string | null;
  videoCodec: string | null;
  audioCodec: string | null;
  audioLangs: string[];
  subtitleLangs: string[];
  /** Subtítulos forzados: solo traducen lo que en la película ya está en otro idioma. Se
   *  enseñan como algo distinto de una subtitulación, porque lo son. */
  forcedSubLangs: string[];
  subsSinEspecificar: boolean;
  ripper: string | null;
  sizeBytes: number | null;
  downloadCount: number | null;
  postedAt: string | null;
  files: FicheroRelease[];
}

export interface Ficha {
  id: string;
  movieId: string;
  /** Con `show` los ficheros de cada versión son episodios y se enseñan por temporada. */
  kind: TitleKind;
  title: string;
  originalTitle: string | null;
  year: number | null;
  imdbId: string | null;
  director: string | null;
  runtimeMin: number | null;
  /** `EE.UU.`, `España`… tal como los escribe HispaShare. De aquí sale el idioma original. */
  countries: string[];
  synopsis: string | null;
  genres: string[];
  castNames: string[];
  posterUrl: string | null;
  imdbRating: number | null;
  library: EstadoBiblioteca;
  releases: ReleaseVista[];
}

export type EstadoDescarga =
  | 'queued' | 'downloading' | 'paused' | 'hashing' | 'completed'
  | 'transcoding' | 'transcode_failed' | 'ready_to_place'
  | 'importing' | 'imported' | 'import_failed' | 'failed' | 'cancelled';

export type OrigenDescarga = 'hispashare' | 'biblioteca';

export interface Descarga {
  id: string;
  status: EstadoDescarga;
  /** De dónde viene el original (D-28). Con `biblioteca` no hay hash ni nada que preguntar
   *  a aMule: es un fichero de Plex mandado al conversor. */
  origen: OrigenDescarga;
  ed2kHash: string | null;
  fileName: string;
  /** El fichero de biblioteca que sustituirá al colocarse (D-28). */
  sustituyeRuta: string | null;
  movieTitle: string | null;
  movieYear: number | null;
  posterUrl: string | null;
  quality: string | null;
  sizeBytes: number | null;
  sizeDone: number;
  progressPct: number;
  speedBps: number;
  sourcesTotal: number;
  sourcesXfer: number;
  errorCode: string | null;
  errorDetail: string | null;
  createdAt: string;
  completedAt: string | null;
  sourcePath: string | null;
  targetPath: string | null;
  importMethod: 'hardlink' | 'copy' | 'move' | null;
  wasRemux: boolean | null;
  originalReaped: boolean;
  stagedPath: string | null;
  stagedKind: 'linked' | 'copied' | 'remux' | 'transcoded' | null;
  /** Película o serie (D-27). Con `show`, qué episodio es y con qué lote se pidió. */
  movieKind: TitleKind;
  movieId: string | null;
  season: number | null;
  episode: number | null;
  episodeEnd: number | null;
  loteId: string | null;
}

/** Lo que devuelve encolar una temporada (D-27): lo creado y lo que se saltó, con motivo. */
export interface ResultadoLote {
  loteId: string | null;
  season: number | null;
  descargas: Descarga[];
  omitidas: FicheroOmitido[];
}

export interface FicheroOmitido {
  fileName: string;
  season: number | null;
  episode: number | null;
  motivo: 'ya_pedida' | 'en_plex' | 'rechazada';
  detalle: string | null;
  downloadId: string | null;
}

/** Un fichero esperando en la carpeta de conversiones a que se elija biblioteca (D-15). */
export interface Conversion {
  downloadId: string;
  origen: OrigenDescarga;
  /** El fichero de biblioteca que sustituirá al colocarse (D-28), si se pidió desde Biblioteca. */
  sustituyeRuta: string | null;
  ed2kHash: string | null;
  /** `importing` es una que se está moviendo AHORA: sigue en la lista mientras dura, que
   *  puede ser minutos si la biblioteca está en otro disco. */
  status: 'ready_to_place' | 'importing' | 'import_failed';
  title: string;
  year: number | null;
  posterUrl: string | null;
  fileName: string;
  path: string;
  tipo: 'linked' | 'copied' | 'remux' | 'transcoded';
  sizeBytes: number | null;
  existe: boolean;
  /** `true` solo mientras se mueve ESTA. Las colocaciones van de una en una, así que varias
   *  pueden estar en `importing` y solo una moviéndose: las demás esperan turno. */
  moviendoAhora: boolean;
  stagedAt: string | null;
  errorDetail: string | null;
  /** Película o episodio (D-27), con su temporada y número. Lo puesto a mano manda. */
  kind: TitleKind;
  season: number | null;
  episode: number | null;
  episodeEnd: number | null;
  /** Ruta relativa a la raíz de la biblioteca: el nombre a secas en una película, con la
   *  carpeta de la serie y la de la temporada en un episodio. `null` si no se puede nombrar. */
  nombreDestino: string | null;
  /** Qué falta para poder nombrarlo, cuando `nombreDestino` es nulo. */
  motivoNoColocable: string | null;
  originalPath: string | null;
  originalReaped: boolean;
  /** Dónde quedó la copia de una colocación ANTERIOR de esta misma película, si sigue ahí.
   *  Solo la tienen las reconvertidas después de colocarse: moverlas a esa misma biblioteca
   *  la sustituye; a otra distinta, deja dos. */
  colocadaEn: string | null;
  /** La biblioteca de esa copia anterior, para poder preseleccionarla. */
  colocadaEnBiblioteca: string | null;
}

export interface BibliotecaDestino {
  id: string;
  title: string;
  /** De películas o de series: cada fichero solo se ofrece a las de su tipo (D-27). */
  type: TitleKind;
  serverName: string;
  localPath: string;
  /** La marcada como destino en Ajustes: se preselecciona, pero se puede cambiar. */
  sugerida: boolean;
}

/** Lo que la watchlist enseña de la descarga de una película: en qué punto va, sin las
 *  rutas ni el historial que sí hacen falta en Descargas. */
export interface DescargaDeWatchlist {
  id: string;
  status: EstadoDescarga;
  ed2kHash: string | null;
  progressPct: number;
  speedBps: number;
  sourcesTotal: number;
  sourcesXfer: number;
  errorCode: string | null;
  createdAt: string;
}

export interface ElementoWatchlist {
  id: string;
  movieId: string | null;
  /** Película o serie (D-27). Una serie se encuentra y se propone; nunca se baja sola. */
  kind: TitleKind;
  hispashareId: number | null;
  imdbId: string | null;
  query: string | null;
  title: string | null;
  year: number | null;
  posterUrl: string | null;
  source: 'manual' | 'plex';
  library: EstadoBiblioteca;
  minQuality: string | null;
  requiredLangs: string[];
  autoDownload: boolean;
  status: 'pending' | 'found' | 'downloading' | 'done' | 'abandoned';
  /** La descarga que hay de esta película, si la hay. No sustituye a `status`: ese cuenta
   *  la vigilancia y este en qué punto va el fichero. */
  descarga: DescargaDeWatchlist | null;
  lastCheckedAt: string | null;
  createdAt: string;
}

export interface ResumenImportacion {
  leidos: number;
  peliculas: number;
  series: number;
  nuevos: number;
  yaEstaban: number;
  /** De las que ya estaban, cuántas cambiaron de título en Plex. */
  retituladas: number;
  sinImdb: number;
}

export interface ResumenVinculacion {
  revisados: number;
  vinculados: number;
  pendientes: number;
  descartadosPorImdb: number;
  sinCandidato: number;
  errores: string[];
}

export interface ServidorPlex {
  id: string;
  name: string;
  baseUrl: string;
  hasToken: true;
  machineIdentifier: string | null;
  version: string | null;
  enabled: boolean;
  lastOkAt: string | null;
  lastError: string | null;
}

export interface PinPlex {
  id: number;
  /** Los cuatro caracteres que se teclean en plex.tv/link. */
  code: string;
  expiresAt: string;
}

export interface BibliotecaPlex {
  id: string;
  serverId: string;
  serverName: string;
  sectionKey: number;
  title: string;
  type: 'movie' | 'show';
  countsAsOwned: boolean;
  importTarget: boolean;
  plexPath: string | null;
  localPath: string | null;
  pathVerifiedAt: string | null;
  pathVerifyResult: VerificacionRutas | null;
}

export interface VerificacionRutas {
  ok: boolean;
  sampledFile: string | null;
  resolvedLocal: string | null;
  exists: boolean;
  writableDir: boolean;
  sameFilesystemAsConversions: boolean;
  importMethod: 'move' | 'copy' | null;
  problema: string | null;
}

export interface AjustesApp {
  transcodeEnabled: boolean;
  transcodeMaxHeight: number;
  transcodeVideoEncoder: 'auto' | 'libx264';
  /** Cuántas conversiones corren a la vez; el resto espera en la cola. */
  transcodeConcurrency: number;
  /** Orden de preferencia de idiomas de pista. Manda en qué orden salen del MP4. */
  transcodeLangs: string[];
  transcodeOnlyPreferredAudio: boolean;
  transcodeForcedDefault: boolean;
  /** Bloques de un mismo subtítulo de imagen leyéndose a la vez (D-19). */
  ocrBlocksAtOnce: number;
  seedDays: number;
  minFreeGb: number;
  defaultMinQuality: string | null;
  defaultLangs: string[];
  /** Qué hace idónea a una versión de HispaShare. Colorea y ordena la ficha, y es lo que
   *  la automática usa para saber cuál es la mejor. */
  preferenciasVersion: PreferenciasVersion;
  /** Hasta dónde llega la descarga automática (D-22). */
  autoModo: 'apagado' | 'simulacion' | 'activo';
  autoActivas: number;
  /** Gigas que la automática deja siempre libres, contando lo ya comprometido. */
  autoMinLibreGb: number;
  autoMinDescargas: number;
  autoFactorSalud: number;
}

/** Una decisión de la descarga automática: qué eligió y por qué. */
export interface DecisionAuto {
  id: string;
  watchlistItemId: string;
  title: string | null;
  hispashareId: number | null;
  result: 'encolada' | 'simulada' | 'recomendada' | 'sin_candidata';
  elinkId: number | null;
  releaseFileId: string | null;
  points: number | null;
  candidates: number;
  brake: string | null;
  reason: string | null;
  createdAt: string;
}

export interface RespuestaAjustes {
  settings: AjustesApp;
  /** `ficheros`/`bytes`: originales que la recogida se llevará sola con el tiempo.
   *  `retenidos`/`bytesRetenidos`: los que NO se tocan hasta colocar su película. */
  originales: { ficheros: number; bytes: number; retenidos: number; bytesRetenidos: number };
  transcodeDisponible: boolean;
  /** Qué formatos de subtítulo de imagen se pueden leer aquí de verdad. */
  ocr: HerramientasOcr | null;
}

export interface ResumenRecogida {
  candidatos: number;
  retirados: number;
  bytesLiberados: number;
  motivo: 'antiguedad' | 'espacio' | 'nada';
  espacioLibreGb: number | null;
  errores: string[];
}

export interface EstadoAmule {
  ecConnected: boolean;
  red: {
    ed2k: { state: string; highId: boolean; serverName: string | null };
    kad: { state: string };
    speeds: { downloadBps: number; uploadBps: number };
    diskFreeBytes: number | null;
  } | null;
  categoria: number | null;
  transferencias: number;
  activas: number;
  sinFuentes: number;
  velocidadTotal: number;
}

export interface Aviso {
  id: string;
  type: string;
  title: string;
  body: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface ResumenBarrido {
  revisados: number;
  encontrados: number;
  encolados: number;
  /** Las que se habrían encolado y no se encolaron: simulación, o sin cupo. */
  simuladas: number;
  errores: string[];
  duracionMs: number;
}

/** Una tarea larga corriendo en el servidor: el barrido o la vinculación.
 *
 *  Se consulta al entrar en la pantalla, así que cambiar de página ya no pierde el hilo:
 *  el trabajo sigue en el servidor y al volver se ve por dónde va. */
interface TareaBase {
  enCurso: boolean;
  fase: string;
  hechos: number;
  total: number;
  empezada: string;
  terminada: string | null;
  error: string | null;
}

/** Unión discriminada por `nombre`: así el resumen se lee sin comprobaciones a mano —cada
 *  tarea tiene el suyo y son distintos. */
export type TareaWatchlist =
  | (TareaBase & { nombre: 'revision'; resumen: ResumenRevisionTarea | null; objeto?: string | null })
  | (TareaBase & { nombre: 'barrido'; resumen: ResumenBarrido | null })
  | (TareaBase & { nombre: 'vinculacion'; resumen: ResumenVinculacion | null });

/** `VITE_DEMO=1`: la interfaz funciona sin ningún servicio detrás (X-5).
 *
 *  Vite sustituye `import.meta.env.VITE_DEMO` por un literal al compilar, así que en una
 *  compilación normal esto es `false` constante, el `import('./demo.js')` de más abajo queda
 *  inalcanzable y el módulo con los datos falsos **ni siquiera se genera**.
 *
 *  La constante NO se exporta, y la copia de `eventos.ts` es deliberada: en cuanto otro
 *  módulo la importa deja de poder plegarse, el `import()` sobrevive a la poda y las seis
 *  películas inventadas acaban en `dist/`. Comprobado: exportándola, salían. */
const modoDemo = import.meta.env.VITE_DEMO === '1';

/** Error con el `code` estable de la API: la interfaz decide qué pintar a partir de él,
 *  nunca del texto del mensaje. */
export class ApiFallo extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Cualquier 401 significa lo mismo en toda la app: no hay sesión. Avisar desde un único
 *  sitio evita que cada pantalla tenga que distinguir «no estás dentro» de «esto falló»,
 *  que es justo la confusión que hacía que un login caducado se leyera como un error de
 *  búsqueda. */
type OyenteSesion = () => void;
const oyentesSesion = new Set<OyenteSesion>();

export function alPerderSesion(o: OyenteSesion): () => void {
  oyentesSesion.add(o);
  return () => oyentesSesion.delete(o);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Modo demo (X-5): se intercepta AQUÍ, en la única puerta por la que sale todo, y no
  // sustituyendo `api` por otro objeto. Un `api` falso paralelo se desincroniza sin que
  // nadie lo note; esto, en cambio, contesta con un error nombrando la ruta que le falta.
  if (modoDemo) {
    const { responderDemo } = await import('./demo.js');
    const r = await responderDemo(path, init);
    if (!r.ok) throw new ApiFallo(r.message, r.code, r.status);
    return r.datos as T;
  }

  // `Content-Type: application/json` SOLO cuando se manda cuerpo. Anunciarlo en un
  // DELETE sin cuerpo hace que el servidor intente parsear vacío y falle con un 400
  // que no tiene nada que ver con lo que se pedía.
  const cabeceras: Record<string, string> = { ...(init?.headers as Record<string, string>) };
  if (init?.body != null) cabeceras['Content-Type'] ??= 'application/json';

  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: cabeceras,
  });
  if (!res.ok) {
    const cuerpo = (await res.json().catch(() => null)) as
      | { error?: { code?: string; message?: string } }
      | null;
    if (res.status === 401 && !path.startsWith('/api/auth/')) {
      for (const o of oyentesSesion) o();
    }
    throw new ApiFallo(
      cuerpo?.error?.message ?? `Error ${res.status}`,
      cuerpo?.error?.code ?? 'unknown',
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  health: () => request<Health>('/api/health'),
  me: () => request<{ authenticated: boolean }>('/api/auth/me'),
  login: (password: string) =>
    request<{ ok: boolean }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  /** `kind` separa películas de series (D-27); el filtro lo aplica HispaShare. */
  buscar: (q: string, kind?: TitleKind) =>
    request<{ items: ResultadoBusqueda[]; total: number; libraryAge: string | null }>(
      `/api/search?q=${encodeURIComponent(q)}${kind ? `&kind=${kind}` : ''}`,
    ),
  /** La búsqueda avanzada, con los filtros del propio HispaShare. `POST` por la forma del
   *  cuerpo —veinte filtros, dos de ellos listas—, no porque cambie nada. */
  buscarAvanzada: (filtros: BusquedaAvanzada) =>
    request<{
      items: ResultadoBusqueda[];
      total: number;
      libraryAge: string | null;
      pagina: number;
      hayMas: boolean;
    }>('/api/search/advanced', { method: 'POST', body: JSON.stringify(filtros) }),
  ficha: (id: string) => request<Ficha>(`/api/movies/${id}`),

  descargas: (todas = false) =>
    request<{ items: Descarga[] }>(`/api/downloads${todas ? '?status=all' : ''}`),
  encolar: (releaseFileId: string) =>
    request<{ download: Descarga }>('/api/downloads', {
      method: 'POST',
      body: JSON.stringify({ releaseFileId }),
    }),
  /** Una temporada entera de un grupo (D-27): una descarga por episodio, y lo que ya estaba
   *  pedido vuelve en `omitidas` en vez de tumbar la petición. */
  encolarTemporada: (elinkId: number, season: number | null) =>
    request<ResultadoLote>('/api/downloads/lote', {
      method: 'POST',
      body: JSON.stringify({ elinkId, season }),
    }),
  pausar: (id: string) =>
    request<{ ok: boolean }>(`/api/downloads/${id}`, { method: 'PATCH', body: JSON.stringify({ action: 'pause' }) }),
  reanudar: (id: string) =>
    request<{ ok: boolean }>(`/api/downloads/${id}`, { method: 'PATCH', body: JSON.stringify({ action: 'resume' }) }),
  /** La papelera de una descarga. Qué hace lo decide su estado, y lo decide el servidor:
   *  mientras está viva se CANCELA —hay que avisar a aMule y la fila se queda en
   *  `cancelled`, que es historial— y una vez terminada desaparece del historial. En los
   *  dos casos `borrarFicheros` es la mitad irreversible, y por eso viaja explícito. */
  borrarDescarga: (id: string, borrarFicheros: boolean) =>
    request<void>(`/api/downloads/${id}?deleteFiles=${borrarFicheros}`, { method: 'DELETE' }),

  /** Lo que aMule sabe de una descarga: partes, disponibilidad y relojes. Se pide solo
   *  mientras alguien tiene el detalle abierto — el mapa de partes son cientos de entradas.
   *  `detalle: null` es «aMule ya no la tiene», no un fallo. */
  detalleAmule: (id: string) =>
    request<{ detalle: TransferDetail | null }>(`/api/downloads/${id}/amule`),

  /** Las fuentes de una descarga, una a una. Aparte del detalle: es otra lista, se mira en
   *  otro momento y no tiene sentido traerla mientras nadie la ha desplegado. */
  fuentesAmule: (id: string) =>
    request<{ fuentes: TransferSource[] }>(`/api/downloads/${id}/amule/sources`),

  reintentarPreparacion: (id: string) =>
    request<{ ok: boolean }>(`/api/downloads/${id}/retry-convert`, { method: 'POST' }),

  /** Vuelve a bajar el original sobre la misma descarga. Para cuando ya no está en disco y
   *  por tanto no se puede reconvertir. */
  volverADescargar: (id: string) =>
    request<{ download: Descarga }>(`/api/downloads/${id}/redownload`, { method: 'POST' }),

  conversiones: () =>
    request<{ items: Conversion[]; libraries: BibliotecaDestino[] }>('/api/conversions'),
  /** La cola de conversión: lo que está pasando por ffmpeg y lo que espera turno. Vive en
   *  memoria de la API, así que se pide entera cada vez que el evento avisa de un cambio. */
  colaConversion: () => request<ColaConversionVista>('/api/conversions/queue'),
  /** Arranca la colocación. Contesta en cuanto está en marcha —no espera al fichero, que
   *  entre discos distintos son varios gigas copiándose— así que un `ok` aquí significa
   *  «empezó», no «ya está». Cómo acaba llega por `download:status`. */
  colocar: (downloadId: string, libraryId: string) =>
    request<{ ok: boolean; enCurso: boolean }>(`/api/conversions/${downloadId}/place`, {
      method: 'POST',
      body: JSON.stringify({ libraryId }),
    }),

  /** Mueve varias a la MISMA biblioteca. El servidor las hace de una en una —entre
   *  montajes distintos cada colocación copia la película entera— y contesta enseguida con
   *  las que arrancaron y las que no: que una de diez no se pueda mover es el caso normal,
   *  y no tiene por qué tumbar a las otras nueve. */
  colocarVarias: (downloadIds: string[], libraryId: string) =>
    request<{ arrancadas: string[]; fallos: { downloadId: string; problema: string }[] }>(
      '/api/conversions/place-many',
      { method: 'POST', body: JSON.stringify({ downloadIds, libraryId }) },
    ),

  /** Temporada y episodio a mano para colocar un fichero de una serie (D-27). Nulos =
   *  volver a lo que dedujo el parser del nombre. */
  fijarEpisodio: (downloadId: string, episodio: { season: number | null; episode: number | null; episodeEnd: number | null }) =>
    request<{ ok: boolean }>(`/api/conversions/${downloadId}/episode`, {
      method: 'POST',
      body: JSON.stringify(episodio),
    }),

  /** Para una conversión, esté bajo ffmpeg o esperando turno. No borra nada: el original
   *  de aMule sigue donde estaba y la descarga vuelve a `completed`, así que volver a
   *  convertirla no descarga un byte. Lo único que se pierde es el rato de ffmpeg hecho. */
  cancelarConversion: (downloadId: string) =>
    request<{ ok: boolean }>(`/api/conversions/${downloadId}/cancel`, { method: 'POST' }),

  /** Descarta lo que espera en conversiones. Con `borrarOriginal` se lleva además el
   *  fichero de aMule, y entonces no hay de dónde reconvertir: por eso viaja aparte. */
  eliminarConversion: (downloadId: string, borrarOriginal: boolean) =>
    request<{ ok: boolean; bytesLiberados: number; originalBorrado: boolean }>(
      `/api/conversions/${downloadId}?deleteOriginal=${borrarOriginal}`,
      { method: 'DELETE' },
    ),

  /** El registro de las conversiones de una descarga, intento a intento.
   *
   *  Se pide por el id de la DESCARGA porque el caso que lo justifica —ffmpeg falló— no
   *  deja ningún fichero en conversiones: por eso lo enseñan las dos pantallas. Lo que
   *  llegue después de esta foto entra por `transcode:log`, sin volver a pedir nada. */
  registroConversion: (downloadId: string) =>
    request<RegistroConversion>(`/api/conversions/${downloadId}/log`),

  /** Las pistas del ORIGINAL de aMule, con la propuesta automática y sus motivos. */
  pistas: (downloadId: string) => request<PistasDescarga>(`/api/conversions/${downloadId}/tracks`),
  /** Guarda el plan editado y vuelve a convertir con él. No descarga nada otra vez. */
  /** Guarda el plan y encola la reconversión. Como `colocar`, vuelve en cuanto arranca:
   *  recodificar puede tardar horas y el resultado llega por `download:status`. */
  reconvertir: (downloadId: string, plan: PlanManual) =>
    request<{ ok: boolean; enCurso: boolean }>(`/api/conversions/${downloadId}/reconvert`, {
      method: 'POST',
      body: JSON.stringify(plan),
    }),
  olvidarPlan: (downloadId: string) =>
    request<{ ok: boolean }>(`/api/conversions/${downloadId}/tracks`, { method: 'DELETE' }),

  /** Qué se puede poner de una descarga en el reproductor, y cómo se servirá cada pista.
   *  El vídeo y los subtítulos no pasan por `request`: los pide el motor del reproductor
   *  (`lib/reproductor.ts`) por su cuenta, porque no son JSON. */
  reproduccion: (downloadId: string) =>
    request<ReproduccionDescarga>(`/api/player/${downloadId}`),

  watchlist: () => request<{ items: ElementoWatchlist[] }>('/api/watchlist'),
  anadirWatchlist: (datos: {
    hispashareId?: number;
    query?: string;
    minQuality?: string | null;
    requiredLangs?: string[] | null;
    autoDownload?: boolean;
  }) => request<{ item: ElementoWatchlist }>('/api/watchlist', { method: 'POST', body: JSON.stringify(datos) }),
  actualizarWatchlist: (id: string, cambios: Record<string, unknown>) =>
    request<{ item: ElementoWatchlist }>(`/api/watchlist/${id}`, { method: 'PATCH', body: JSON.stringify(cambios) }),
  borrarWatchlist: (id: string) => request<void>(`/api/watchlist/${id}`, { method: 'DELETE' }),
  /** `todas` reabre lo ya revisado antes de barrer: es cómo se repite una simulación
   *  entera. Sin él solo se miran las nuevas y las que llevan una semana sin revisarse. */
  barrerWatchlist: (todas = false) =>
    request<{ task: TareaWatchlist }>(`/api/watchlist/sweep${todas ? '?todas=1' : ''}`, {
      method: 'POST',
    }),
  /** Qué está corriendo, o cómo acabó lo último. `task` es `null` si nunca se lanzó nada. */
  tareaWatchlist: () => request<{ task: TareaWatchlist | null }>('/api/watchlist/task'),
  importarDePlex: () => request<ResumenImportacion>('/api/watchlist/import-plex', { method: 'POST' }),
  vincularFichas: () =>
    request<{ task: TareaWatchlist }>('/api/watchlist/link', { method: 'POST' }),
  /** Vinculación a mano de UN elemento con la ficha que elija quien mira. Sirve también
   *  para corregir una vinculación que el cruce automático hizo mal. */
  vincularAMano: (id: string, hispashareId: string) =>
    request<{ item: ElementoWatchlist; imdbDiscrepa: boolean }>(`/api/watchlist/${id}/link`, {
      method: 'POST',
      body: JSON.stringify({ hispashareId }),
    }),

  // ── Revisión de una biblioteca (D-28) ──
  revision: (libraryId: string | null) =>
    request<{ libraries: BibliotecaRevisable[]; items: FilaRevision[]; resumen: ResumenRevision | null }>(
      `/api/library/review${libraryId ? `?libraryId=${encodeURIComponent(libraryId)}` : ''}`,
    ),
  /** Arranca la revisión por el mismo carril que el barrido: contesta con la tarea. */
  lanzarRevision: (libraryId: string, todas = false) =>
    request<{ task: TareaWatchlist }>(
      `/api/library/review?libraryId=${encodeURIComponent(libraryId)}${todas ? '&todas=1' : ''}`,
      { method: 'POST' },
    ),
  tareaRevision: () => request<{ task: TareaWatchlist | null }>('/api/library/review/task'),
  convertirRevision: (id: string) =>
    request<{ item: FilaRevision }>(`/api/library/review/${id}/convert`, { method: 'POST' }),
  redescargarRevision: (id: string) =>
    request<{ item: FilaRevision }>(`/api/library/review/${id}/redownload`, { method: 'POST' }),
  revisarFila: (id: string) =>
    request<{ item: FilaRevision }>(`/api/library/review/${id}/recheck`, { method: 'POST' }),
  vincularRevision: (id: string, hispashareId: string) =>
    request<{ item: FilaRevision }>(`/api/library/review/${id}/link`, {
      method: 'POST',
      body: JSON.stringify({ hispashareId }),
    }),
  /** Borra las Plex Versions de una película: lo que Plex creó al «Optimizar». */
  borrarVersionesPlex: (id: string) =>
    request<{ borradas: number; bytes: number; fallos: string[] }>(`/api/library/review/${id}/plex-versions/delete`, {
      method: 'POST',
    }),
  revisionEnBloque: (ids: string[], accion: 'convert' | 'redownload' | 'plex-versions-delete') =>
    request<{ hechas: string[]; fallos: { id: string; motivo: string }[] }>(
      `/api/library/review/${accion}-many`,
      { method: 'POST', body: JSON.stringify({ ids }) },
    ),

  servidoresPlex: () => request<{ servers: ServidorPlex[] }>('/api/settings/plex/servers'),
  anadirServidorPlex: (datos: { name: string; baseUrl: string; token: string }) =>
    request<{ server: ServidorPlex }>('/api/settings/plex/servers', {
      method: 'POST',
      body: JSON.stringify(datos),
    }),
  borrarServidorPlex: (id: string) =>
    request<void>(`/api/settings/plex/servers/${id}`, { method: 'DELETE' }),
  /** Vinculación por PIN: se pide un código, lo teclea una persona en plex.tv/link y se
   *  pregunta en bucle hasta que aparezca el token. */
  vincularPlex: (id: string) =>
    request<{ pin: PinPlex }>(`/api/settings/plex/servers/${id}/link`, { method: 'POST' }),
  comprobarVinculacionPlex: (id: string, pinId: number) =>
    request<{ pendiente: boolean; server?: ServidorPlex }>(
      `/api/settings/plex/servers/${id}/link/${pinId}`,
      { method: 'POST' },
    ),
  /** El token es de solo escritura: se manda uno nuevo, nunca se lee el que hay. Se
   *  reemplaza en vez de borrar el servidor porque borrarlo se lleva sus bibliotecas. */
  reemplazarTokenPlex: (id: string, token: string) =>
    request<{ server: ServidorPlex }>(`/api/settings/plex/servers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ token }),
    }),
  probarServidorPlex: (id: string) =>
    request<{ ok: boolean; version: string | null }>(`/api/settings/plex/servers/${id}/test`, { method: 'POST' }),
  redescubrirPlex: (id: string) =>
    request<{ libraries: BibliotecaPlex[] }>(`/api/settings/plex/servers/${id}/discover`, { method: 'POST' }),

  bibliotecasPlex: () => request<{ libraries: BibliotecaPlex[] }>('/api/settings/plex/libraries'),
  actualizarBiblioteca: (id: string, cambios: Record<string, unknown>) =>
    request<{ library: BibliotecaPlex }>(`/api/settings/plex/libraries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(cambios),
    }),
  verificarRutas: (id: string) =>
    request<VerificacionRutas>(`/api/settings/plex/libraries/${id}/verify-path`, { method: 'POST' }),
  sincronizarPlex: () => request<{ items: number; bibliotecas: number }>('/api/library/sync', { method: 'POST' }),

  estadoAmule: () => request<EstadoAmule>('/api/amule/status'),
  estadisticasAmule: () => request<{ estadisticas: TransferStats }>('/api/amule/stats'),
  /** La historia de velocidades de la ventana pedida, en segundos, resumida a los puntos
   *  que se van a pintar. */
  clientesConCredito: () => request<ResumenCreditos>('/api/amule/known-clients'),
  historialVelocidad: (ventanaSegundos: number, puntos = 300) =>
    request<{ ventanaSegundos: number; historia: SpeedHistory }>(
      `/api/amule/speed-history?ventana=${ventanaSegundos}&puntos=${puntos}`,
    ),
  decisionesAuto: () => request<{ decisions: DecisionAuto[] }>('/api/watchlist/decisions'),
  ajustes: () => request<RespuestaAjustes>('/api/settings'),
  guardarAjustes: (cambios: Partial<AjustesApp>) =>
    request<{ settings: AjustesApp }>('/api/settings', { method: 'PATCH', body: JSON.stringify(cambios) }),
  recogerOriginales: () => request<ResumenRecogida>('/api/maintenance/reap', { method: 'POST' }),
  almacenamiento: () => request<Almacenamiento>('/api/storage'),

  avisos: (soloNoLeidas = false) =>
    request<{ items: Aviso[]; unread: number }>(`/api/notifications${soloNoLeidas ? '?unread=true' : ''}`),
  marcarLeido: (id: string) => request<{ ok: boolean }>(`/api/notifications/${id}/read`, { method: 'POST' }),
  marcarTodoLeido: () => request<{ ok: boolean }>('/api/notifications/read-all', { method: 'POST' }),
};
